import { createQueryFilters } from 'context/DataContext';
import { CashFlowsValidValues } from 'features/drilldown/cashflows/configurations/cashflow-inputs';
import {
  ChartFragments,
  MonthlyTableFragments,
  PortfolioSummaryMetrics,
  SummaryTableFragments,
} from 'features/drilldown/cashflows/gql/cashflowDataFragments';
import { ASSET_CLASS_IDS, AssetClassId } from 'types/assetClass';

import { gql, useQuery } from '@apollo/client';

import { Filter } from '__generated__/globalTypes';

import { GetCashflowByPool, GetCashflowByPoolVariables } from './__generated__/GetCashflowByPool';

export const POOL_CASHFLOW = gql`
  query GetCashflowByPool(
    $poolId: ID!
    $cdr: Float!
    $cpr: Float!
    $lsr: Float!
    $daysDelay: Int
    $filters: [Filter!]
    $rateType: RateType
    $servicingRate: Float
    $prepaymentAssumptionType: PrepaymentAssumptionType
    $defaultAssumptionType: DefaultAssumptionType
  ) {
    user {
      id
      company {
        id
        pool(id: $poolId) {
          id
          performance_summary(filters: $filters) {
            cashFlows: cash_flows(
              conditionalDefaultRate: $cdr
              conditionalPrepaymentRate: $cpr
              lossSeverityRate: $lsr
              prepaymentAssumptionType: $prepaymentAssumptionType
              defaultAssumptionType: $defaultAssumptionType
              servicingRate: $servicingRate
              daysDelay: $daysDelay
              rateType: $rateType
            ) {
              monthlyCashFlows: monthly_cash_flows {
                ...ChartCashFlowMonth
                ...MonthlyTableCashFlowMonth
              }
              price_yield_matrix {
                spread_matrix
                yield_matrix
              }
              totals {
                ...SummaryTableCashFlowTotals
              }
            }
            ...PortfolioSummaryMetrics
            ...SummaryTablePerformanceSummary
          }
        }
      }
    }
  }
  ${PortfolioSummaryMetrics.performanceSummaryMetrics}
  ${MonthlyTableFragments.cashFlowMonth}
  ${ChartFragments.cashFlowMonth}
  ${SummaryTableFragments.cashFlowTotals}
  ${SummaryTableFragments.performanceSummary}
`;

export const getUseSelectedPoolCashflow = (assetClassId: AssetClassId, filters: Filter[]) => {
  const assetClass = ASSET_CLASS_IDS[assetClassId];

  const useSelectedPoolCashflow_gql = (poolId: string, userInputs: CashFlowsValidValues) => {
    const { loading, error, data } = useQuery<GetCashflowByPool, GetCashflowByPoolVariables>(
      POOL_CASHFLOW,
      {
        variables: {
          cdr: userInputs.cdr / 100,
          cpr: userInputs.cpr / 100,
          lsr: userInputs.lsr / 100,
          daysDelay: +userInputs.daysDelay,
          servicingRate: userInputs.servicingRate != null ? userInputs.servicingRate / 100 : null,
          rateType: userInputs.rateType,
          prepaymentAssumptionType: userInputs.prepaymentAssumptionType,
          defaultAssumptionType: userInputs.defaultAssumptionType,
          poolId,
          filters: createQueryFilters(assetClass, filters),
        },
        skip: !poolId,
      },
    );

    return {
      loading,
      error,
      data: data?.user?.company?.pool?.performance_summary,
    };
  };

  return useSelectedPoolCashflow_gql;
};
